import { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = '/api';

export function normalizeSymbol(symbol) {
  return symbol.endsWith('.NS') ? symbol : `${symbol}.NS`;
}

export function useWatchlist() {
  const [watchlists, setWatchlists] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWatchlists = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/watchlist`);
      const data = res.data || [];
      try {
        const savedOrder = JSON.parse(localStorage.getItem('wl_order') || '[]');
        if (savedOrder.length) {
          data.sort((a, b) => {
            const ai = savedOrder.indexOf(a.id);
            const bi = savedOrder.indexOf(b.id);
            if (ai === -1 && bi === -1) return 0;
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi;
          });
        }
      } catch {}
      setWatchlists(data);
      return data;
    } catch {
      console.error('Failed to load watchlists');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const addToWatchlist = async (watchlistId, symbol) => {
    const ns = normalizeSymbol(symbol);
    const target = watchlists.find(wl => wl.id === watchlistId);
    if (!target) return false;
    if ((target.stocks || []).includes(ns)) {
      toast.error('Stock already in watchlist');
      return false;
    }
    try {
      await axios.post(`${API_URL}/watchlist/${watchlistId}/add`, { symbol: ns });
      setWatchlists(prev => prev.map(wl =>
        wl.id === watchlistId
          ? { ...wl, stocks: [...(wl.stocks || []), ns] }
          : wl
      ));
      toast.success(`Added ${ns.replace('.NS', '')} to ${target.name}`);
      return true;
    } catch {
      toast.error('Failed to add stock');
      return false;
    }
  };

  const removeFromWatchlist = async (watchlistId, symbol) => {
    const ns = normalizeSymbol(symbol);
    try {
      await axios.delete(`${API_URL}/watchlist/${watchlistId}/remove/${ns}`);
      setWatchlists(prev => prev.map(wl =>
        wl.id === watchlistId
          ? { ...wl, stocks: (wl.stocks || []).filter(s => s !== ns), stocks_data: (wl.stocks_data || []).filter(s => s.symbol !== ns) }
          : wl
      ));
      toast.success('Removed from watchlist');
    } catch {
      toast.error('Failed to remove stock');
    }
  };

  const createWatchlist = async (name) => {
    if (!name.trim()) { toast.error('Enter watchlist name'); return null; }
    try {
      const res = await axios.post(`${API_URL}/watchlist`, { name: name.trim() });
      const newWl = { ...res.data, stocks: [], stocks_data: [] };
      setWatchlists(prev => [...prev, newWl]);
      toast.success('Watchlist created');
      return newWl;
    } catch {
      toast.error('Failed to create watchlist');
      return null;
    }
  };

  const renameWatchlist = async (watchlistId, name) => {
    if (!name.trim()) return;
    try {
      await axios.put(`${API_URL}/watchlist/${watchlistId}`, { name: name.trim() });
      setWatchlists(prev => prev.map(wl => wl.id === watchlistId ? { ...wl, name: name.trim() } : wl));
      toast.success('Renamed');
    } catch {
      toast.error('Failed to rename');
    }
  };

  const deleteWatchlist = async (watchlistId) => {
    try {
      await axios.delete(`${API_URL}/watchlist/${watchlistId}`);
      setWatchlists(prev => prev.filter(wl => wl.id !== watchlistId));
      toast.success('Watchlist deleted');
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete watchlist');
      return false;
    }
  };

  const reorderWatchlists = (fromIdx, toIdx) => {
    setWatchlists(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      localStorage.setItem('wl_order', JSON.stringify(next.map(w => w.id)));
      return next;
    });
  };

  const isInWatchlist = (symbol) => {
    const ns = normalizeSymbol(symbol);
    return watchlists.some(wl => (wl.stocks || []).includes(ns));
  };

  return {
    watchlists, setWatchlists, loading,
    fetchWatchlists,
    addToWatchlist,
    removeFromWatchlist,
    createWatchlist,
    renameWatchlist,
    deleteWatchlist,
    reorderWatchlists,
    isInWatchlist,
  };
}
